import React, { useState } from 'react'
import { Modal, Button, Input, Label, Icon, Text, Title } from '@nimbus-ds/components'
import { ExclamationCircleIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@nimbus-ds/icons'
import { ga4Service } from '@/api/ga4'
import { useToast } from '@/hooks/use-toast'

interface GA4ConfigurationModalProps {
  storeId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

type Step = 'input' | 'instructions' | 'verification'

export const GA4ConfigurationModal: React.FC<GA4ConfigurationModalProps> = ({
  storeId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<Step>('input')
  const [propertyId, setPropertyId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  // App-wide service account email (from manual setup)
  const SERVICE_ACCOUNT_EMAIL = 'ecommercelab-ga4-integration@ecommercelab-472420.iam.gserviceaccount.com'

  const handleSaveConfiguration = async () => {
    if (!propertyId || !ga4Service.validatePropertyId(propertyId)) {
      toast({
        title: 'Invalid Property ID',
        description: 'Please enter a valid GA4 property ID (numbers only)',
        variant: 'destructive'
      })
      return
    }

    setIsLoading(true)
    try {
      const response = await ga4Service.saveConfig(storeId, propertyId)
      if (response.success) {
        setStep('instructions')
      } else {
        toast({
          title: 'Configuration Failed',
          description: response.message || 'Failed to save configuration',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleTestConnection = async () => {
    setIsLoading(true)
    try {
      const result = await ga4Service.testAccess(storeId)
      if (result.success && result.valid) {
        setStep('verification')
        toast({
          title: 'Success!',
          description: `GA4 connected successfully. ${result.sessions_last_7_days || 0} sessions in the last 7 days.`,
        })
        // Call onSuccess to refresh the parent component and close modal
        onSuccess()
        // Auto-close modal after showing success for 3 seconds
        setTimeout(() => {
          handleClose()
        }, 3000)
      } else {
        toast({
          title: 'Connection Failed',
          description: result.error || 'Please verify you have granted Viewer access to the service account in GA4',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Test Failed',
        description: 'Could not test GA4 connection. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setStep('input')
    setPropertyId('')
    onClose()
  }

  return (
    <Modal open={isOpen} onDismiss={handleClose}>
      {step === 'input' && (
        <>
          <Modal.Header title="Configure Google Analytics 4" />
          <Modal.Body>
            <Text>Connect your GA4 property to get advanced conversion analytics</Text>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="property-id">GA4 Property ID</Label>
                <Input
                  id="property-id"
                  placeholder="123456789"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  disabled={isLoading}
                />
                <Text fontSize="caption">
                  Find this in GA4 → Admin → Property Details
                </Text>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                    <Icon source={<ExclamationCircleIcon />} color="currentColor" />
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100">How it works:</p>
                    <ol className="mt-2 list-decimal list-inside space-y-1 text-blue-800 dark:text-blue-200">
                      <li>Enter your GA4 Property ID</li>
                      <li>Grant read access to our service account</li>
                      <li>Start seeing real conversion data</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button appearance="neutral" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSaveConfiguration}
              disabled={!propertyId || isLoading}
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </Button>
          </Modal.Footer>
        </>
      )}

      {step === 'instructions' && (
        <>
          <Modal.Header title="Grant Access in Google Analytics" />
          <Modal.Body>
            <Text>One more step to complete the connection</Text>

            <div className="space-y-4 py-4">
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                <Title as="h4" className="mb-3">Follow these steps in GA4:</Title>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start">
                    <span className="font-medium mr-2">1.</span>
                    <span>Go to <strong>GA4 → Admin → Property Access Management</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">2.</span>
                    <span>Click <strong>"+ Add users"</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">3.</span>
                    <div>
                      <span>Add this email:</span>
                      <div className="mt-1 p-2 bg-white dark:bg-gray-800 rounded border font-mono text-xs break-all">
                        {SERVICE_ACCOUNT_EMAIL}
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">4.</span>
                    <span>Select role: <strong>"Viewer"</strong> (read-only access)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">5.</span>
                    <span>Click <strong>"Add"</strong></span>
                  </li>
                </ol>
              </div>

              <div className="flex items-start space-x-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 p-3 rounded-lg">
                <div className="mt-0.5 flex-shrink-0">
                  <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
                </div>
                <p>
                  Only you can grant this access. TN-Xperiment will only be able to read
                  analytics data you explicitly authorize.
                </p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button appearance="neutral" onClick={() => setStep('input')} disabled={isLoading}>
              Back
            </Button>
            <Button appearance="primary" onClick={handleTestConnection} disabled={isLoading}>
              {isLoading ? 'Testing...' : "I've granted access"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {step === 'verification' && (
        <>
          <Modal.Header title="GA4 Connected Successfully!" />
          <Modal.Body>
            <div className="py-6">
              <div className="flex items-center space-x-3 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950 p-4 rounded-lg">
                <Icon source={<CheckCircleIcon />} color="currentColor" />
                <div>
                  <p className="font-medium">Connection verified</p>
                  <p className="text-sm mt-1">
                    You can now view GA4 analytics in your experiments
                  </p>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button appearance="primary" onClick={handleClose}>
              Go to Dashboard
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}
